import { useState } from 'react';

import PropTypes from 'prop-types';

import Button from '#globalComponents/Button/Button';
import Select from '#globalComponents/Forms/Select';
import Textarea from '#globalComponents/Forms/Textarea';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import { useOrgMembersBasic } from '#orgFeature/models/org/queries';

import { useVisibleFieldDefs } from '../../../models/customFields/queries';
import { useCreateDeal } from '../../../models/deals/mutations';
import CustomFieldsSection from '../../common/CustomFieldsSection/CustomFieldsSection';

import { buildDealPayload } from './buildDealPayload';
import CompanyAutocomplete from './CompanyAutocomplete';

const CompanyCreationFields = ({ defs, values, onChange }) => (
	<div className="border border-bgBase-500 rounded-card p-3 bg-bgBase-300 space-y-1.5">
		<div className="text-[10px] font-semibold uppercase tracking-wider text-bgBase-800 mb-1">
			Pflichtangaben zum Unternehmen
		</div>
		{defs.map((def) => {
			const key = def.isDefault ? def.fieldKey : def.id;
			return (
				<div key={def.id}>
					<TextInput
						type={def.fieldType === 'number' ? 'number' : 'text'}
						value={values[key] ?? ''}
						onChange={(e) => onChange(key, e.target.value)}
						placeholder={`${def.name} *`}
					/>
				</div>
			);
		})}
	</div>
);

CompanyCreationFields.propTypes = {
	defs: PropTypes.array.isRequired,
	values: PropTypes.object.isRequired,
	onChange: PropTypes.func.isRequired,
};

const NewDealModal = ({ isOpen, onClose, pipelineId, stages, crmCompanyId, companyName: prefillCompanyName }) => {
	const [title, setTitle] = useState('');
	const [companyInput, setCompanyInput] = useState(prefillCompanyName ?? '');
	const [selectedCompany, setSelectedCompany] = useState(
		crmCompanyId ? { id: crmCompanyId, companyName: prefillCompanyName ?? '' } : null,
	);
	// Deals are only created into active stages — picking a won/lost stage here
	// would immediately close the deal (and stamp actualCloseAt), which is never
	// the intent when adding a new deal.
	const firstActiveStageId =
		[...(stages ?? [])].sort((a, b) => a.stageOrder - b.stageOrder).find((s) => s.stageType === 'active')?.id ?? '';
	const [stageId, setStageId] = useState(firstActiveStageId);
	const [dealValue, setDealValue] = useState('');
	const [expectedCloseAt, setExpectedCloseAt] = useState('');
	const [assignedUserId, setAssignedUserId] = useState('');
	const [description, setDescription] = useState('');
	const [customFields, setCustomFields] = useState({});
	const [newCompanyFields, setNewCompanyFields] = useState({});

	const createDeal = useCreateDeal();
	const { data: dealFieldDefs = [] } = useVisibleFieldDefs('deal');
	const { data: companyFieldDefs = [] } = useVisibleFieldDefs('company');
	const { data: membersData } = useOrgMembersBasic();
	const members = membersData?.members ?? [];

	const isNewCompany = !selectedCompany && companyInput.trim().length > 0;
	const newCompanyRequiredDefs = companyFieldDefs.filter((f) => f.isRequired && f.fieldKey !== 'companyName');
	const selectableStages = [...(stages ?? [])]
		.sort((a, b) => a.stageOrder - b.stageOrder)
		.filter((s) => s.stageType === 'active');
	const companyName = companyInput.trim();

	const handleSubmit = (e) => {
		e.preventDefault();
		if (!title.trim() || !companyName || !stageId) return;

		const payload = buildDealPayload({
			title,
			companyName,
			selectedCompany,
			pipelineId,
			stageId,
			dealValue,
			expectedCloseAt,
			assignedUserId,
			description,
			customFields,
			isNewCompany,
			newCompanyRequiredDefs,
			newCompanyFields,
		});
		createDeal.mutate(payload, { onSuccess: onClose });
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} as="form" onSubmit={handleSubmit} size="m">
			<Panel.Header onClose={onClose} subtitle="Erstellen Sie einen neuen Deal in Ihrer Pipeline">
				Neuer Deal
			</Panel.Header>
			<Panel.Body className="space-y-4">
				<div>
					<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
						Titel *
					</label>
					<TextInput
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						placeholder="z. B. Angebot Website-Relaunch"
						required
						autoFocus
					/>
				</div>

				{!crmCompanyId && (
					<CompanyAutocomplete
						value={companyInput}
						selectedCompany={selectedCompany}
						onChange={setCompanyInput}
						onSelect={(company) => {
							setSelectedCompany(company);
							setCompanyInput(company.companyName);
							setNewCompanyFields({});
						}}
						onClear={() => {
							setSelectedCompany(null);
							setCompanyInput('');
						}}
					/>
				)}

				{isNewCompany && !crmCompanyId && newCompanyRequiredDefs.length > 0 && (
					<CompanyCreationFields
						defs={newCompanyRequiredDefs}
						values={newCompanyFields}
						onChange={(key, value) => setNewCompanyFields((prev) => ({ ...prev, [key]: value }))}
					/>
				)}

				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
							Stage
						</label>
						<Select value={stageId} onChange={(e) => setStageId(e.target.value)}>
							{selectableStages.map((s) => (
								<option key={s.id} value={s.id}>
									{s.name}
								</option>
							))}
						</Select>
					</div>
					<div>
						<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
							Wert (&euro;)
						</label>
						<TextInput
							type="number"
							min="0"
							step="0.01"
							value={dealValue}
							onChange={(e) => setDealValue(e.target.value)}
							placeholder="0"
						/>
					</div>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div>
						<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
							Voraussichtlicher Abschluss
						</label>
						<TextInput
							type="date"
							value={expectedCloseAt}
							onChange={(e) => setExpectedCloseAt(e.target.value)}
						/>
					</div>
					<div>
						<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
							Zugewiesen an
						</label>
						<Select value={assignedUserId} onChange={(e) => setAssignedUserId(e.target.value)}>
							<option value="">Nicht zugewiesen</option>
							{members.map((m) => (
								<option key={m.userId} value={m.userId}>
									{m.fullName ?? m.email}
								</option>
							))}
						</Select>
					</div>
				</div>

				<div>
					<label className="block text-xs font-semibold text-text uppercase tracking-wide mb-1.5">
						Beschreibung
					</label>
					<Textarea
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						rows={2}
						placeholder="Optionale Notizen zum Deal ..."
						className="!resize-none"
					/>
				</div>

				{dealFieldDefs.some((f) => !f.isDefault) && (
					<CustomFieldsSection
						entityType="deal"
						values={customFields}
						inline
						onChange={(fieldId, value) => setCustomFields((cf) => ({ ...cf, [fieldId]: value }))}
					/>
				)}
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button type="button" variant="outline" size="sm" onClick={onClose}>
					Abbrechen
				</Button>
				<Button
					type="submit"
					variant="solid"
					size="sm"
					loading={createDeal.isPending}
					disabled={!title.trim() || !companyName || !stageId}
				>
					Deal erstellen
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

NewDealModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	pipelineId: PropTypes.string.isRequired,
	stages: PropTypes.array.isRequired,
	crmCompanyId: PropTypes.string,
	companyName: PropTypes.string,
};

export default NewDealModal;
